import React from "react";
import clsx from "clsx";
import { SpaceAuthor } from "types/spaceTypes";
import UserAvatarService from "services/userAvatarService";
import { defaultSynthMember } from "../constants/defaultSynthMember";

interface SpaceMessageAvatarProps {
    author: SpaceAuthor;
    size?: 'sm' | 'md' | 'lg';
    showRole?: boolean;
}

export default function SpaceMessageAvatar({ 
    author, 
    size = 'md', 
    showRole = false 
}: SpaceMessageAvatarProps) {
    // Safety check for undefined author or missing id
    if (!author || !author.id) {
        console.warn('SpaceMessageAvatar: author or author.id is undefined', author);
        return null;
    }
    const sizeClasses = {
        sm: 'w-6 h-6 text-xs',
        md: 'w-8 h-8 text-sm',
        lg: 'w-10 h-10 text-base'
    };

    const roleColors = {
        system: 'bg-gradient-to-tr from-[#56B648] to-[#FFB902]',
        admin: 'bg-blue-500',
        member: 'bg-slate-400',
        guest: 'bg-orange-400'
    };

    // Special handling for AI agents (including Space Synth AI)
    const isAIAgent = author.id === "synth-ai" || author.id === "synth" || author.name === "Synth AI" || author.name === "Synth" || author.role === "ai";
    
    // Get user avatar data from service (API-ready)
    const userAvatarData = UserAvatarService.getUserAvatarSync(author.id);
    const avatarUrl = isAIAgent 
        ? defaultSynthMember.image // Use the Synth logo from codebase
        : (userAvatarData?.avatarUrl || author.avatar);
    const fallbackColor = userAvatarData?.fallbackColor || UserAvatarService.getFallbackColor(author.id);
    const initials = UserAvatarService.generateInitials(author.name);

    return (
        <div className="flex items-center gap-2">
            <div
                className={clsx(
                    'rounded-full flex items-center justify-center text-white font-medium overflow-hidden',
                    sizeClasses[size]
                )}
                style={{
                    backgroundColor: avatarUrl ? (isAIAgent ? '#042821' : 'transparent') : (author.role === 'system' ? 'transparent' : fallbackColor)
                }}
            >
                {avatarUrl ? (
                    <img 
                        src={avatarUrl} 
                        alt={author.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            // Fallback to initials if image fails to load
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentElement!.innerHTML = initials;
                            e.currentTarget.parentElement!.style.backgroundColor = fallbackColor;
                        }}
                    />
                ) : author.role === 'system' ? (
                    <div className="bg-gradient-to-tr from-[#56B648] to-[#FFB902] w-full h-full flex items-center justify-center rounded-full">
                        <i className="ri-robot-line text-[#042821]"></i>
                    </div>
                ) : (
                    initials
                )}
            </div>
            {showRole && author.role && (
                <span className={clsx(
                    'text-xs px-2 py-0.5 rounded-full',
                    {
                        'bg-blue-100 text-blue-700': author.role === 'admin',
                        'bg-gray-100 text-gray-700': author.role === 'member',
                        'bg-orange-100 text-orange-700': author.role === 'guest',
                        'bg-green-100 text-green-700': author.role === 'system',
                        'bg-yellow-100 text-yellow-700': author.role === 'ai'
                    }
                )}>
                    {author.role}
                </span>
            )}
        </div>
    );
}
